<template>
  <section class="vat-registration" aria-labelledby="vat-registration-title" data-testid="vat-registration-panel">
    <div>
      <h2 id="vat-registration-title">Учёт НДС</h2>
      <p>Период отражения документа в книге покупок или продаж.</p>
    </div>
    <q-form class="vat-registration__form" @submit.prevent="save">
      <q-select v-model="period" :options="periodOptions" emit-value map-options :disable="readOnly || saving" outlined dense label="Отразить в НДС за период" />
      <template v-if="incoming">
        <q-checkbox v-model="deductionAccepted" label="Принять НДС к вычету" :disable="readOnly || saving" data-testid="vat-deduction-accepted" />
        <q-input v-model="acceptedDate" outlined dense type="date" label="Дата принятия на учёт" :disable="readOnly || saving" :error="Boolean(dateError)" :error-message="dateError" :hint="requiresAcceptedDate ? 'Проверьте дату принятия покупки на учёт' : 'Для авансового счёта-фактуры дата необязательна'" />
      </template>
      <p v-if="readOnly" class="vat-registration__readonly">{{ readOnlyReason }}</p>
      <div v-if="message" :class="status === 'error' ? 'text-negative' : 'text-positive'" role="status" data-testid="vat-registration-status">{{ message }}</div>
      <ul v-if="diagnostics.length" class="text-negative">
        <li v-for="(item, index) in diagnostics" :key="index">{{ item.message || item.code }}</li>
      </ul>
      <q-btn type="submit" outline no-caps color="primary" label="Сохранить учёт НДС" :disable="readOnly" :loading="saving" data-testid="vat-registration-save" />
    </q-form>
  </section>
</template>

<script>
import { buildNdsPeriodOptions } from '../../services/ndsPeriod'
import { parseDate } from '../../services/dateFormat'

export default {
  name: 'VatRegistrationPanel',
  props: {
    document: { type: Object, required: true },
    historyId: { type: [String, Number], default: 'last' }
  },
  emits: ['reload'],
  data() {
    return { period: null, periodOptions: [], deductionAccepted: false, acceptedDate: '', saving: false, message: '', status: '', dateError: '', diagnostics: [], requestId: '', timer: null, documentOrgId: null }
  },
  computed: {
    incoming() { return ['in', 'incoming'].includes(this.document.direction || this.document.body?.document_direction) },
    requiresAcceptedDate() { return this.document.document_type === 'upd' },
    isHistorical() { return this.historyId !== null && this.historyId !== undefined && this.historyId !== '' && String(this.historyId) !== 'last' },
    readOnly() { return !this.document.id || String(this.documentOrgId) !== String(this.$store.state.current_org_id) || this.$store.state.permissions.edit_fin_documents !== true || this.isHistorical || this.document.is_active === false || this.$store.state.connection_status === 'offline' },
    readOnlyReason() {
      if (this.isHistorical) return 'Историческая версия доступна только для чтения.'
      if (this.document.is_active === false) return 'Архивный документ доступен только для чтения.'
      if (this.$store.state.connection_status === 'offline') return 'Нет подключения. Сохранение будет доступно после восстановления связи.'
      if (String(this.documentOrgId) !== String(this.$store.state.current_org_id)) return 'Обновите документ после смены организации.'
      return 'Для изменения учёта НДС нужно право редактирования бухгалтерских документов.'
    },
    contextKey() { return JSON.stringify([this.document.id, this.historyId, this.$store.state.current_org_id]) }
  },
  watch: {
    document: { immediate: true, handler: 'onDocumentUpdated' },
    contextKey() { this.cancelRequest(); this.resetFromDocument() },
    readOnly(value) { if (value) this.resetFromDocument() }
  },
  mounted() {
    this.emitter.on('onUpdateDocumentNdsState', this.onResponse)
    this.emitter.on('accessDeniedErrorReceived', this.onResponse)
  },
  beforeUnmount() {
    this.cancelRequest()
    this.emitter.off('onUpdateDocumentNdsState', this.onResponse)
    this.emitter.off('accessDeniedErrorReceived', this.onResponse)
  },
  methods: {
    onDocumentUpdated() { this.documentOrgId = this.$store.state.current_org_id; this.resetFromDocument() },
    cancelRequest() { clearTimeout(this.timer); this.timer = null; this.requestId = ''; this.saving = false },
    resetFromDocument() {
      this.cancelRequest()
      const body = this.document.body || {}
      const nds = this.document.nds_period
      this.period = typeof nds === 'object' && nds !== null ? nds.value : nds || null
      this.periodOptions = buildNdsPeriodOptions()
      if (this.period && !this.periodOptions.some((item) => String(item.value) === String(this.period))) this.periodOptions.push({ value: this.period, label: nds?.label || String(this.period) })
      const flag = Object.prototype.hasOwnProperty.call(body, 'accept_vat_deduction') ? body.accept_vat_deduction : body.nds_deduction
      this.deductionAccepted = flag === true || flag === 1 || ['true', '1', 'да'].includes(String(flag).toLowerCase())
      const dateField = ['accepted_date', 'acceptance_date', 'date_accepted', 'date_rec'].find((field) => Object.prototype.hasOwnProperty.call(body, field))
      const date = parseDate(dateField ? body[dateField] : null)
      this.acceptedDate = date.isValid() ? date.format('YYYY-MM-DD') : ''
      this.message = ''; this.status = ''; this.dateError = ''; this.diagnostics = []
    },
    save() {
      if (this.readOnly || this.saving) return
      this.message = ''; this.dateError = ''; this.diagnostics = []
      const date = parseDate(this.acceptedDate)
      if (this.incoming && ((this.requiresAcceptedDate && this.deductionAccepted && !this.acceptedDate) || (this.acceptedDate && !date.isValid()))) {
        this.dateError = 'Укажите корректную дату принятия на учёт'
        return
      }
      this.requestId = this.connection.makeRequestId('vat-registration')
      this.saving = true
      this.status = 'pending'
      this.timer = setTimeout(() => {
        this.cancelRequest()
        this.status = 'error'
        this.message = 'Сервер не ответил. Повторите сохранение учёта НДС.'
      }, 30000)
      const registration = this.incoming ? {
        deduction_accepted: this.deductionAccepted,
        accepted_date: this.acceptedDate ? date.format('YYYY-MM-DD') : null
      } : undefined
      this.connection.updateDocumentNdsState(this.requestId, this.document.id, this.period, registration, 'last')
    },
    onResponse(response) {
      if (!this.requestId || response.request_id !== this.requestId) return
      this.cancelRequest()
      if (response.error || response.type === 'error') {
        this.status = 'error'
        this.message = response.message || (response.error === 'access_denied' ? 'Недостаточно прав для изменения учёта НДС.' : 'Не удалось сохранить учёт НДС.')
        const details = response.details || response.error_info?.details || {}
        this.diagnostics = Array.isArray(details) ? details : details.diagnostics || []
        const dateDiagnostic = this.diagnostics.find((item) => /accepted_date/.test(item.field_path || item.field || ''))
        this.dateError = dateDiagnostic?.message || ''
        return
      }
      this.status = 'success'
      this.message = 'Учёт НДС сохранён.'
      this.$q.notify({ type: 'positive', message: this.message })
      this.$emit('reload')
    }
  }
}
</script>

<style scoped>
.vat-registration { display: grid; grid-template-columns: minmax(0, 1fr) minmax(240px, 360px); gap: var(--sid-space-5); align-items: start; }
.vat-registration h2 { margin: 0; font-size: 1.25rem; }
.vat-registration p { margin: var(--sid-space-1) 0 0; color: var(--sid-color-text-secondary); }
.vat-registration__form { display: grid; gap: var(--sid-space-3); min-width: 0; }
.vat-registration__form .q-btn { justify-self: start; }
.vat-registration__readonly { font-size: var(--sid-font-size-label); }
@media (max-width: 767px) { .vat-registration { grid-template-columns: minmax(0, 1fr); } }
</style>
